import { useEffect, useState, useCallback } from 'react';

export interface BroadcastMessage {
  type: string;
  message: string;
  [key: string]: string;
}

// eslint-disable-next-line max-len
const useBroadcastChannel = <T extends BroadcastMessage = BroadcastMessage>(channelName: string) => {
  const [channel, setChannel] = useState<BroadcastChannel | null>(null);
  const [message, setMessage] = useState<T | null>(null);
  const [isChannelOpen, setIsChannelOpen] = useState(false);

  // Initialize the BroadcastChannel when the component mounts
  useEffect(() => {
    const newChannel = new BroadcastChannel(channelName);
    setChannel(newChannel);
    setIsChannelOpen(true);

    // Clean up the channel when the component unmounts
    return () => {
      newChannel.close();
      setIsChannelOpen(false);
    };
  }, [channelName]);

  // Listen for messages from the channel
  useEffect(() => {
    if (channel) {
      const handleMessage = (event: MessageEvent<T>) => {
        setMessage(event.data);
      };

      channel.onmessage = handleMessage;

      // Clean up event listener when the channel changes
      return () => {
        channel.onmessage = null;
      };
    }

    return undefined;
  }, [channel]);

  // Function to post a message to the channel
  const postMessage = useCallback(
    (msg: T) => {
      if (channel && isChannelOpen) {
        channel.postMessage(msg);
      }
    },
    [channel, isChannelOpen],
  );

  return { message, postMessage };
};

export default useBroadcastChannel;
